/** /users/ — all three user kinds: list, retrieve, patch (+avatar), custom actions. */
import type { BatchResponse } from './batch';
import type { BookingTag } from './bookings';
import type { CountMode, CountOnlyResult } from './countStatus';
import type { DashboardMfaPolicy } from './dashboardMfa';
import { apiDelete, apiGet, apiPatch, apiPost, apiPut, client } from './client';
import type { AiDiagStartBody, DiagnoseLaunchContext } from '@/features/ai-diag/types';
import type { TenantIdsParam } from '@/scope/tenantIds';
import type { TourOperatorEnvironment } from './tourOperators';

export type UserKind = 'system' | 'staff' | 'guest';

export interface TenantRef {
  id: number;
  name: string;
  code: string;
  /** `Tour_Operator.kind` — the Tenant cell marks a sandbox (plan D8). */
  environment?: TourOperatorEnvironment | null;
}

export interface MFADevice {
  id: number;
  kind: 'totp' | 'sms' | 'email';
  name: string;
  target_masked?: string;
  confirmed: boolean;
}

export interface ConsoleUserListItem {
  id: number;
  kind: UserKind;
  login: string;
  email: string;
  phone: string | null;
  /** Nullable on the model (users/models.py:182-183): imported guests often have neither. */
  first_name: string | null;
  last_name: string | null;
  tour_operator: TenantRef | null;
  is_active: boolean;
  is_staff: boolean;
  is_deleted: boolean;
  is_employee: boolean;
  email_verified: boolean;
  phone_verified: boolean;
  last_login: string | null;
  date_joined: string;
  mfa_enrolled: boolean;
  groups: { id: number; name: string }[];
  /** null for staff/guest rows (console-specific). */
  permissions_count: number | null;
  active_tokens_count: number;
  /** null for staff/guest rows (console-specific). */
  pending_invite_count: number | null;
  /** Account-level tags (CP-8095) — facts about the PERSON, not about one
   *  booking. Same wire shape as a booking tag. */
  tags: BookingTag[];
}

export interface ConsoleUserDetail extends ConsoleUserListItem {
  permissions: string[];
  devices: MFADevice[];
  avatar: { original: string; medium: string; small: string } | null;
  is_superuser: boolean;
  lang: string | null;
  allow_email_nf: boolean;
  allow_sms_nf: boolean;
  allow_whatsapp_nf: boolean;
  freeze_email: boolean;
  freeze_phone: boolean;
  tour_operator_users: {
    id: number;
    tour_operator: TenantRef;
    roles: { id: number; name: string }[];
  }[];
  magic_link: { present: boolean; expires: string | null } | null;
  /** Dashboard access groups the person is in — direct membership, their staff roles'
   *  groups, or the tenant's guest group. Sorted by name; empty without a tour operator. */
  access_groups: UserAccessGroupRef[];
  /** Can we reach this person? One verdict per channel, from the delivery pipeline's own
   *  gates (never re-derived here). */
  reachability: UserReachability;
  /** Read-only technical properties (mirrors the dashboard INTERNAL PROPERTIES panel). */
  internal: UserInternalProps;
  /** Read-only locale / OTA / platform / status / consent attributes. */
  attributes: UserAttributes;
}

export interface UserAccessGroupRef {
  id: number;
  name: string;
}

/** `will_receive` — every delivery gate passes; `blocked` — a gate stops delivery;
 *  `unconfirmed` — nothing stops it, but delivery cannot be confirmed. */
export type ReachabilityVerdict = 'will_receive' | 'blocked' | 'unconfirmed';

/** One channel's verdict. `reason_code` is null exactly when the verdict is
 *  `will_receive`; `reason` is that code in words (null with it). */
export interface ReachabilityEntry<Code extends string> {
  verdict: ReachabilityVerdict;
  reason_code: Code | null;
  reason: string | null;
}

export type EmailReachabilityCode =
  | 'no_tenant'
  | 'placeholder_address'
  | 'phone_login'
  | 'opted_out'
  | 'no_address'
  | 'ota_suppressed'
  | 'noreply_address'
  | 'invalid_address'
  | 'ota_relay'
  | 'ota_shared_inbox'
  | 'check_failed';

export type SmsReachabilityCode =
  | 'no_tenant'
  | 'no_phone'
  | 'invalid_phone'
  | 'opted_out'
  | 'carrier_opt_out'
  | 'permanent_failure'
  | 'shared_phone'
  | 'repeated_failures'
  | 'check_failed';

export type WhatsappReachabilityCode =
  | 'no_tenant'
  | 'no_phone'
  | 'invalid_phone'
  | 'opted_out'
  | 'carrier_opt_out'
  | 'no_whatsapp'
  | 'shared_phone'
  | 'whatsapp_unknown'
  | 'check_failed';

export type PushReachabilityCode =
  | 'no_tenant'
  | 'app_not_installed'
  | 'opted_out'
  | 'device_notifications_off'
  | 'no_chat_identity'
  | 'check_failed';

export type InAppChatReachabilityCode =
  | 'excluded_from_chat'
  | 'no_conversation'
  | 'conversation_removed'
  | 'app_not_installed'
  | 'no_chat_identity'
  | 'conversation_archived'
  | 'wrong_licensee'
  | 'check_failed';

/** The person's Main Office conversation — a real lookup (never created, no Twilio call). */
export interface UserMainOffice {
  /** `active` — live; `archived` — idle (a new message reopens it); `removed` — every
   *  participant was removed. */
  state: 'active' | 'archived' | 'removed';
  /** Twilio_Conversation id. */
  conversation_id: number;
  unique_name: string;
  /** The tour operator whose Main Office channel holds the conversation. */
  tour_operator: TenantRef;
  in_expected_channel: boolean;
}

export interface UserReachability {
  /** `address` — what delivery uses (`get_clean_email()`), whatever the verdict. */
  email: ReachabilityEntry<EmailReachabilityCode> & { address: string | null };
  /** `address` — the number delivery dials (E.164). */
  sms: ReachabilityEntry<SmsReachabilityCode> & { address: string | null };
  whatsapp: ReachabilityEntry<WhatsappReachabilityCode> & { address: string | null };
  push: ReachabilityEntry<PushReachabilityCode>;
  in_app_chat: ReachabilityEntry<InAppChatReachabilityCode> & {
    has_identity: boolean;
    /** Whose Main Office channel the person belongs in (the licensee under a franchise). */
    expected_tour_operator: TenantRef | null;
    main_office: UserMainOffice | null;
  };
}

export type ReachabilityChannel = keyof UserReachability;

export interface UserInternalProps {
  passport_number: string | null;
  date_of_birth: string | null;
  address: string | null;
  deep_link: string | null;
  twilio_identity: string | null;
  main_office_conversation: string | null;
  trip_count: number;
  bypass_magic_auth: boolean;
  exclude_from_chat: boolean;
  exclude_from_group_chat: boolean;
  has_admin_access: boolean;
  is_admin: boolean | null;
  is_guest: boolean | null;
  is_rbac_user: boolean | null;
  is_tour_operator_staff: boolean;
  sms_is_undeliverable: boolean | null;
  sms_allowed_via_twilio: boolean;
  whatsapp_allowed_via_twilio: boolean;
  whatsapp_status: string | null;
  roles: string[];
}

export interface UserAttributes {
  lang: string | null;
  country: string | null;
  platform: string | null;
  ota: { id: number; name: string | null } | null;
  ota_domain: { id: number; name: string | null } | null;
  ota_code: string | null;
  ota_email: string | null;
  email_is_fake: boolean;
  airbnb_user_id: string | null;
  notifications_status: boolean | null;
  location_status: string | null;
  date_joined: string | null;
  consents: {
    privacy_policy: boolean;
    email: boolean;
    sms: boolean;
    whatsapp: boolean;
    push: boolean;
    airbnb: boolean;
    ads: boolean;
  };
}

// Back-compat aliases (pre-kinds naming).
export type SystemUserListItem = ConsoleUserListItem;
export type SystemUserDetail = ConsoleUserDetail;

export interface ListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  kind?: UserKind;
  /** One id or a comma list of ids (D1). */
  tour_operator?: TenantIdsParam;
  include_deleted?: boolean;
  is_active?: boolean;
  is_staff?: boolean;
  has_mfa?: boolean;
  group?: string;
  email_contains?: string;
  phone_contains?: string;
  first_name_contains?: string;
  last_name_contains?: string;
  /** Override the endpoint's own row-total mode. Omit to use the server default
   *  (`deferred` for guests) — that omission is what keeps a cold load free. */
  count_mode?: CountMode;
  /** Ask only for the total; no page, no rows. */
  count_only?: boolean;
}

export interface PaginatedResponse<T> {
  /** Total matching rows, or **-1** when unknown — the normal case in the
   *  `async`/`deferred` count modes. Never treat -1 as a quantity; read it
   *  through `useCountMode` / `COUNT_UNKNOWN`. */
  count: number;
  /** Which count mode the endpoint actually ran (it decides, not the client). */
  count_mode?: CountMode;
  /** Whether another page exists. Present in `async`/`deferred` only; derived
   *  from one extra probed row rather than a COUNT. */
  has_next?: boolean;
  /** Signed handle to poll at `/count_status/`; non-null in `async` only. */
  async_count_handle?: string | null;
  next: string | null;
  previous: string | null;
  results: T[];
}

function toQuery(params: ListParams): string {
  const usp = new URLSearchParams();
  // The backend paginates with DRF LimitOffsetPagination — page/page_size
  // were silently ignored before this was fixed.
  const pageSize = params.pageSize ?? 25;
  usp.set('limit', String(pageSize));
  if (params.page && params.page > 1) usp.set('offset', String((params.page - 1) * pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.kind) usp.set('kind', params.kind);
  if (params.tour_operator !== undefined) usp.set('tour_operator', String(params.tour_operator));
  if (params.include_deleted) usp.set('include_deleted', 'true');
  if (params.is_active !== undefined) usp.set('is_active', String(params.is_active));
  if (params.is_staff !== undefined) usp.set('is_staff', String(params.is_staff));
  if (params.has_mfa !== undefined) usp.set('has_mfa', String(params.has_mfa));
  if (params.group) usp.set('group', params.group);
  if (params.count_mode) usp.set('count_mode', params.count_mode);
  if (params.count_only) usp.set('count_only', '1');
  for (const k of ['email_contains', 'phone_contains', 'first_name_contains', 'last_name_contains'] as const) {
    const v = params[k];
    if (v) usp.set(k, v);
  }
  return usp.toString();
}

export async function list(params: ListParams = {}): Promise<PaginatedResponse<ConsoleUserListItem>> {
  const qs = toQuery(params);
  return apiGet(`/users/${qs ? `?${qs}` : ''}`);
}

/** Total only, for the grid's on-demand row count. `async` so the count runs in
 *  Celery rather than holding a web worker on a 5M-row table. */
export async function count(params: ListParams = {}): Promise<CountOnlyResult> {
  const qs = toQuery({ ...params, count_only: true, count_mode: 'async' });
  return apiGet(`/users/?${qs}`);
}

/** The users detail's query key (the record page, its tabs, the editor's re-seed). */
export const userDetailKey = (id: number) => ['consoleUsers', 'detail', id] as const;

export async function retrieve(id: number): Promise<ConsoleUserDetail> {
  return apiGet(`/users/${id}/`);
}

/** The user PATCH writes the avatar only (O2): every profile field is edited through the
 *  user record changeset (`api/userRecord.ts`), and a body naming any other field is
 *  refused whole. */
export interface ProfilePatch {
  /** `null` removes the current avatar. */
  avatar: null;
}

export async function patch(id: number, body: ProfilePatch): Promise<ConsoleUserDetail> {
  return apiPatch(`/users/${id}/`, body);
}

/** Sets the avatar: the file as a multipart part, nothing else (the optional `_json` part
 *  may carry no field, so it is left out). */
export async function patchWithAvatar(id: number, avatar: File): Promise<ConsoleUserDetail> {
  const fd = new FormData();
  fd.append('avatar', avatar);
  const r = await client.patch<ConsoleUserDetail>(`/users/${id}/`, fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return r.data;
}

export async function softDelete(id: number): Promise<void> {
  await apiDelete<void>(`/users/${id}/`);
}

export async function restore(id: number): Promise<void> {
  await apiPost(`/users/${id}/restore/`);
}

/** Irreversible. `reason` (O3) is required — 10 to 1000 characters after trimming (a 400
 *  keyed by `reason` otherwise, nothing scrubbed) — and recorded on the `user.anonymized`
 *  audit row. */
export async function anonymize(id: number, reason: string): Promise<{ ok: boolean; email: string }> {
  return apiPost(`/users/${id}/anonymize/`, { reason });
}

export async function invite(body: {
  email: string;
  first_name?: string;
  last_name?: string;
  group_ids?: number[];
}): Promise<{ user_id: number; invite_code: string; invite_expires_ts: string }> {
  return apiPost('/users/invite/', body);
}

export async function revokeTokens(id: number): Promise<{ revoked: number }> {
  return apiPost(`/users/${id}/revoke_tokens/`);
}

// ---- password management ----

export async function setPassword(
  id: number,
  password: string,
  revokeSessions = true,
): Promise<{ ok: boolean; tokens_revoked: number }> {
  return apiPost(`/users/${id}/set_password/`, { password, revoke_sessions: revokeSessions });
}

export type ResetChannel = 'email' | 'sms';

export async function sendPasswordReset(
  id: number,
  channel: ResetChannel,
): Promise<{ ok: boolean; channel: ResetChannel; expires_ts: string }> {
  return apiPost(`/users/${id}/send_password_reset/`, { channel });
}

// ---- console permissions / groups (system users) ----

export async function grantPermission(id: number, codename: string): Promise<void> {
  await apiPost(`/users/${id}/grant_permission/`, { codename });
}

export async function revokePermission(id: number, codename: string): Promise<void> {
  await apiPost(`/users/${id}/revoke_permission/`, { codename });
}

export async function setGroups(id: number, groupIds: number[]): Promise<void> {
  await apiPut(`/users/${id}/groups/`, { group_ids: groupIds });
}

export async function disableMfa(id: number): Promise<{ ok: boolean; tokens_revoked: number }> {
  return apiPost(`/users/${id}/disable_mfa/`);
}

// ---- dashboard (Access Group) permissions ----

export interface DashboardPermissions {
  flags: {
    is_active: boolean;
    is_staff: boolean;
    is_superuser: boolean;
    is_deleted: boolean;
    is_employee: boolean;
  };
  tour_operator_users: ConsoleUserDetail['tour_operator_users'];
  /** True when perms derive from the tenant's shared guest access group. */
  is_guest: boolean;
  /** The shared guest group (when is_guest); `shared` false for franchise guests. */
  guest_group: { id: number; name: string; shared: boolean } | null;
  /** Redis key backing `cache` — the shared `…-guest` key for non-franchise guests. */
  cache_key: string | null;
  /** Raw Redis cache payload; null = cache empty/missing (never self-healed here). */
  cache: PermissionDict | null;
  /** Recomputed from Access_Permission_Scope rows in the DB. */
  effective: PermissionDict | null;
  agroups: {
    id: number;
    name: string;
    full_access: boolean;
    managed: boolean;
    tour_operator: TenantRef;
    licensee: { id: number; name: string } | null;
  }[];
  tagmap: Record<string, { id: number; name: string }>;
}

export interface PermissionDict {
  full_access: boolean;
  scoped_all_tags: Record<string, Record<string, boolean>>;
  perms: Record<string, Record<string, number[]>>;
}

export async function dashboardPermissions(id: number): Promise<DashboardPermissions> {
  return apiGet(`/users/${id}/dashboard_permissions/`);
}

export async function refreshPermissionCache(id: number): Promise<void> {
  await apiPost(`/users/${id}/refresh_permission_cache/`);
}

export async function clearPermissionCache(id: number): Promise<void> {
  await apiPost(`/users/${id}/clear_permission_cache/`);
}

export async function revokeStaff(id: number): Promise<{ ok: boolean; tou_removed: number }> {
  return apiPost(`/users/${id}/revoke_staff/`);
}

// ---- contacts ----

export interface UserContact {
  id: number;
  type: number;
  type_display: string;
  contact: string;
  tier: number;
  tier_display: string;
  verified: boolean;
  enabled: boolean;
  sequence: number;
  is_primary: boolean;
  created_ts: string;
  updated_ts: string;
  ota: { id: number; name: string } | null;
}

export interface UserAgroup {
  id: number;
  name: string;
  full_access: boolean;
  tour_operator: { id: number; name: string; code: string };
}

/** Lightweight AG membership — safe for real-time hover previews (PK-indexed
 *  M2M; skips the heavy dashboard_permissions diagnostics). */
export async function fetchAgroups(userId: number): Promise<{ results: UserAgroup[] }> {
  return apiGet(`/users/${userId}/agroups/`);
}

export interface ContactListParams {
  page?: number;
  pageSize?: number;
  ordering?: string;
  search?: string;
  contact_contains?: string;
  type?: number;
  verified?: boolean;
  enabled?: boolean;
}

/** Read-only: contacts are written through the user record changeset (its `contacts`
 *  field) — the one-step create / update / delete / make-primary endpoints were retired.
 *  The record pages read each stored contact's OTA and dates from here. */
export async function listContacts(
  userId: number,
  params: ContactListParams = {},
): Promise<PaginatedResponse<UserContact>> {
  const usp = new URLSearchParams();
  const pageSize = params.pageSize ?? 25;
  usp.set('limit', String(pageSize));
  if (params.page && params.page > 1) usp.set('offset', String((params.page - 1) * pageSize));
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.search) usp.set('search', params.search);
  if (params.contact_contains) usp.set('contact_contains', params.contact_contains);
  if (params.type !== undefined) usp.set('type', String(params.type));
  if (params.verified !== undefined) usp.set('verified', String(params.verified));
  if (params.enabled !== undefined) usp.set('enabled', String(params.enabled));
  return apiGet(`/users/${userId}/contacts/?${usp.toString()}`);
}

// ---- MFA management (admin-driven) ----

export interface MFAOverview {
  enabled: boolean;
  primary_method: 'totp' | 'sms' | 'email' | null;
  mfa_enabled_ts: string | null;
  backup_codes_generated_ts: string | null;
  devices: MFADevice[];
  methods: string[];
  /** Unused recovery (backup) codes — one store for the console's and the dashboard's codes. */
  recovery_codes_remaining: number;
  /** Staff: a confirmed authenticator app or phone number, what the dashboard's password
   *  sign-in asks for (e-mail devices are not used there). Null for a system user. */
  dashboard_enrolled: boolean | null;
  /** Staff: their tenant's effective dashboard two-step sign-in. Null for a system user. */
  dashboard_mfa_policy: DashboardMfaPolicy | null;
}

export async function mfaOverview(userId: number): Promise<MFAOverview> {
  return apiGet(`/users/${userId}/mfa/`);
}

export async function mfaEnrollTotp(
  userId: number,
  name?: string,
): Promise<{ device_id: number; secret: string; provisioning_uri: string; qr_svg_b64: string }> {
  return apiPost(`/users/${userId}/mfa/enroll/totp/`, { name });
}

export async function mfaEnrollTotpConfirm(
  userId: number,
  deviceId: number,
  code: string,
): Promise<{ ok: boolean; device: MFADevice }> {
  return apiPost(`/users/${userId}/mfa/enroll/totp/confirm/`, { device_id: deviceId, code });
}

export async function mfaEnrollSms(userId: number, phone: string): Promise<{ device_id: number }> {
  return apiPost(`/users/${userId}/mfa/enroll/sms/`, { phone });
}

export async function mfaEnrollSmsConfirm(
  userId: number,
  deviceId: number,
  code: string,
): Promise<{ ok: boolean; device: MFADevice }> {
  return apiPost(`/users/${userId}/mfa/enroll/sms/confirm/`, { device_id: deviceId, code });
}

export async function mfaEnrollEmail(userId: number, email?: string): Promise<{ device_id: number }> {
  return apiPost(`/users/${userId}/mfa/enroll/email/`, { email });
}

export async function mfaEnrollEmailConfirm(
  userId: number,
  deviceId: number,
  code: string,
): Promise<{ ok: boolean; device: MFADevice }> {
  return apiPost(`/users/${userId}/mfa/enroll/email/confirm/`, { device_id: deviceId, code });
}

export async function mfaDeleteDevice(
  userId: number,
  deviceId: number,
  kind: MFADevice['kind'],
): Promise<void> {
  await apiDelete<void>(`/users/${userId}/mfa/devices/${deviceId}/?kind=${kind}`);
}

export async function mfaSetPrimary(
  userId: number,
  deviceId: number,
  kind: MFADevice['kind'],
): Promise<{ ok: boolean; primary_method: string }> {
  return apiPost(`/users/${userId}/mfa/devices/${deviceId}/set_primary/`, { kind });
}

/** Batch actions over the users grid go through the shared async framework
 *  (ConsoleBatchMixin → background task). A 202 means "queued", not "done" —
 *  track progress in Background Tasks. Grid callers use ResourceDataGrid's batch
 *  menu; this thin helper is for one-off per-user dispatches (e.g. the detail
 *  page's "Re-issue invite"). See `@/api/batch` for the generic client. */
export type UserBatchAction =
  | 'activate'
  | 'deactivate'
  | 'anonymize'
  | 'reset_password'
  | 'reset_mfa'
  | 'resend_invite'
  | 'promote'
  | 'depromote'
  | 'delete';

export async function batch(
  action: UserBatchAction,
  ids: number[],
  params?: Record<string, unknown>,
): Promise<BatchResponse> {
  return apiPost('/users/batch/', {
    action,
    selection: { mode: 'ids', ids },
    ...(params ? { params } : {}),
  });
}

// ---- AI diagnostics (perm: console_entity_ai; shared entity_diagnose tool) --

export async function diagnoseLaunchContext(): Promise<DiagnoseLaunchContext> {
  return apiGet('/users/diagnose/launch_context/');
}

/** POST /users/{id}/diagnose/ — same protocol as the bookings variant.
 *  Soft-deleted users stay diagnosable. */
export async function startDiagnose(
  id: number,
  body: AiDiagStartBody,
): Promise<{ task_id: string; queued: boolean }> {
  return apiPost(`/users/${id}/diagnose/`, body);
}
